import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Navbar } from '../components/Navbar'
import { PropertyCard } from '../components/PropertyCard'
import { propertiesAPI } from '../lib/api'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Search, Building2, Loader2, X } from 'lucide-react'

export function MarketplacePage() {
  const { t } = useTranslation()
  const [properties, setProperties] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [propertyType, setPropertyType] = useState('all')
  const [sortBy, setSortBy] = useState('newest')

  const propertyTypes = [
    { value: 'all', label: t('marketplace.allTypes') },
    { value: 'residential', label: t('marketplace.residential') },
    { value: 'commercial', label: t('marketplace.commercial') },
    { value: 'industrial', label: t('marketplace.industrial') },
    { value: 'mixed_use', label: t('marketplace.mixedUse') },
  ]

  const sortOptions = [
    { value: 'newest', label: t('marketplace.newestFirst') },
    { value: 'yield_high', label: t('marketplace.highestYield') },
    { value: 'price_low', label: t('marketplace.lowestInvestment') },
    { value: 'price_high', label: t('marketplace.highestValue') },
  ]

  useEffect(() => {
    const loadProperties = async () => {
      setLoading(true)
      try {
        const params = {}
        if (propertyType !== 'all') params.property_type = propertyType
        if (searchQuery) params.city = searchQuery
        const res = await propertiesAPI.list(params)
        let data = Array.isArray(res.data) ? res.data : []

        if (sortBy === 'yield_high') data = data.sort((a, b) => b.expected_yield - a.expected_yield)
        else if (sortBy === 'price_low') data = data.sort((a, b) => a.min_investment - b.min_investment)
        else if (sortBy === 'price_high') data = data.sort((a, b) => b.total_value - a.total_value)

        setProperties(data)
      } catch (error) {
        console.error('Failed to load properties:', error)
      } finally {
        setLoading(false)
      }
    }
    loadProperties()
  }, [propertyType, sortBy, searchQuery])

  const clearFilters = () => {
    setSearchQuery('')
    setPropertyType('all')
    setSortBy('newest')
  }

  const hasActiveFilters = searchQuery || propertyType !== 'all' || sortBy !== 'newest'

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      
      <main className="pt-28 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <motion.span initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="inline-block px-4 py-2 border border-brand-primary/50 bg-brand-primary/10 text-brand-primary text-xs font-semibold uppercase tracking-widest mb-4">
              {t('marketplace.badge')}
            </motion.span>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="text-3xl md:text-4xl font-heading text-brand-secondary">
              {t('marketplace.title')}
            </motion.h1>
            <motion.div initial={{ opacity: 0, scaleX: 0 }} animate={{ opacity: 1, scaleX: 1 }} transition={{ delay: 0.2 }}
              className="w-16 h-px bg-brand-primary mx-auto my-6" />
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="text-slate-500">
              {t('marketplace.subtitle')}
            </motion.p>
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="mb-8 p-6 bg-white border border-slate-100">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('marketplace.searchPlaceholder')}
                  className="pl-10 h-12 bg-white border-slate-200 focus:border-brand-primary text-brand-secondary placeholder:text-slate-400 rounded-none"
                  data-testid="search-input" />
              </div>

              <Select value={propertyType} onValueChange={setPropertyType}>
                <SelectTrigger className="w-full lg:w-48 h-12 bg-white border-slate-200 text-brand-secondary rounded-none" data-testid="property-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {propertyTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value} className="text-brand-secondary hover:bg-brand-primary/10">{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full lg:w-48 h-12 bg-white border-slate-200 text-brand-secondary rounded-none" data-testid="sort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {sortOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value} className="text-brand-secondary hover:bg-brand-primary/10">{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {hasActiveFilters && (
                <Button onClick={clearFilters} variant="ghost"
                  className="h-12 text-slate-500 hover:text-brand-secondary hover:bg-slate-50 rounded-none" data-testid="clear-filters-btn">
                  <X className="w-4 h-4 mr-2" />{t('marketplace.clear')}
                </Button>
              )}
            </div>
          </motion.div>

          <div className="mb-6 flex items-center justify-between">
            <p className="text-sm text-slate-500">
              {loading ? t('common.loading') : `${properties.length} ${t('marketplace.propertiesFound')}`}
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
            </div>
          ) : properties.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="properties-grid">
              {properties.map((property, index) => (
                <PropertyCard key={property.id} property={property} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-24 bg-white border border-slate-100" data-testid="no-results">
              <Building2 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-xl font-heading text-brand-secondary mb-2">{t('marketplace.noProperties')}</h3>
              <p className="text-slate-500 mb-6">{t('marketplace.noPropertiesSubtitle')}</p>
              {hasActiveFilters && (
                <Button onClick={clearFilters} variant="outline" className="border-slate-200 text-brand-secondary hover:bg-slate-50 rounded-none">
                  {t('marketplace.clearFilters')}
                </Button>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
